import { useMemo } from 'react';
import { Box, Stack, Typography, useTheme, Divider } from '@mui/material';
import { formatHour } from '@/utils/formatters';

const ORDER = [
  { key: 'Monday', label: 'Mandag' },
  { key: 'Tuesday', label: 'Tirsdag' },
  { key: 'Wednesday', label: 'Onsdag' },
  { key: 'Thursday', label: 'Torsdag' },
  { key: 'Friday', label: 'Fredag' },
  { key: 'Saturday', label: 'Lørdag' },
  { key: 'Sunday', label: 'Søndag' },
];

export default function OccupancyDaysCard({ daysOfWeek }) {
  const theme = useTheme();
  const items = useMemo(
    () => ORDER.map((d) => ({ ...d, ...daysOfWeek?.[d.key] })),
    [daysOfWeek],
  );
  if (!daysOfWeek) return null;

  return (
    <Box
      sx={{
        minHeight: 240,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
      }}
    >
      <Stack divider={<Divider flexItem />} spacing={0}>
        {items.map(({ key, label, occupancy, start_time, end_time }) => {
          // Bar logic: 0-24 hours, bar is 100% width
          const total = 24;
          const start = typeof start_time === 'number' ? start_time : 0;
          const end = typeof end_time === 'number' ? end_time : 0;
          const leftPercent = (start / total) * 100;
          const filledPercent = ((end - start) / total) * 100;
          const rightPercent = 100 - leftPercent - filledPercent;
          return (
            <Box
              key={key}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                py: 0.5,
                borderRadius: 1,
              }}
            >
              <Typography sx={{ minWidth: 80, fontWeight: 500 }}>
                {label}
              </Typography>
              {occupancy ? (
                <Box
                  sx={{
                    flex: 1,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                  }}
                >
                  <Typography variant="body2" sx={{ minWidth: 38 }}>
                    {formatHour(start)}
                  </Typography>
                  <Box
                    sx={{
                      position: 'relative',
                      height: 14,
                      width: '100%',
                      minWidth: 100,
                      borderRadius: 7,
                      background: theme.palette.grey[200],
                      overflow: 'hidden',
                      display: 'flex',
                    }}
                  >
                    {/* Empty before */}
                    <Box sx={{ width: `${leftPercent}%`, height: '100%' }} />
                    {/* Filled */}
                    <Box
                      sx={{
                        width: `${filledPercent}%`,
                        height: '100%',
                        background: theme.palette.primary.main,
                        transition: 'width 0.2s',
                      }}
                    />
                    {/* Empty after */}
                    <Box sx={{ width: `${rightPercent}%`, height: '100%' }} />
                  </Box>
                  <Typography
                    variant="body2"
                    sx={{ minWidth: 38, textAlign: 'right' }}
                  >
                    {formatHour(end)}
                  </Typography>
                </Box>
              ) : (
                <Box
                  sx={{
                    flex: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Typography
                    color="text.secondary"
                    sx={{ fontStyle: 'italic' }}
                  >
                    Ikke aktiv
                  </Typography>
                </Box>
              )}
            </Box>
          );
        })}
      </Stack>
    </Box>
  );
}
